import type {
  Answers,
  RecapSegment,
  SignalBand,
  WeightlossMilestone,
  WeightlossV2Result,
} from "./_schema";

const BASE_SIGNAL_SCORE = 48;
const MAX_SIGNAL_SCORE = 93;
/** The only live band boundary -- see `signalBand` for why the lower two went. */
const VERY_STRONG_SIGNAL_SCORE = 71;
const DAY_MS = 24 * 60 * 60 * 1000;

const FAT_LOCATIONS: Record<string, { label: string; prefix: string }> = {
  belly: {
    label: "your belly and lower gut",
    prefix: "You told us you carry most of your fat around ",
  },
  chest: {
    label: "your chest has gone soft or puffy",
    prefix: "You told us ",
  },
  "love-handles": {
    label: "your love handles and lower back",
    prefix: "You told us you carry most of your fat around ",
  },
  everywhere: {
    label: "fat spread across your body",
    prefix: "You told us you've noticed ",
  },
};

const DURATION_LABELS: Record<string, string> = {
  years: "more than a few years",
  year: "about a year",
  months: "the past few months",
  unsure: "an uncertain length of time",
};

const TRIED_LABELS: Record<string, string> = {
  fasting: "intermittent fasting",
  keto: "keto or low-carb",
  calories: "counting calories",
  medication: "weight-loss supplements or medication",
};

const GOAL_LABELS: Record<string, string> = {
  belly: "a flatter belly",
  chest: "a firmer-looking chest",
  waist: "a leaner waist",
  energy: "more energy and drive",
};

const SIGNAL_BULLETS = [
  "Where you carry the most fat",
  "How long you've been fighting it",
  "The way your body responds when you diet",
] as const;

const MILESTONE_CONTENT = [
  {
    label: "Week 1",
    offsetDays: 6,
    title: "Ignition",
    body: "Settle into the routine, five caps a day. Start watching your energy, your digestion, and how your waistband feels. Some men notice the first 1–3 lbs start to move.",
  },
  {
    label: "Week 3",
    offsetDays: 20,
    body: "Pants feel a little looser and you tug at your shirt less. This is often the point where someone close asks if you're doing something different this time.",
  },
  {
    label: "Day 30",
    offsetDays: 29,
    title: "Breaking Orbit",
    body: "Check the notch on your belt and how your shirts sit across the chest. Many men say the belly is finally starting to respond after years of nothing.",
  },
  {
    label: "Day 60",
    offsetDays: 59,
    body: "The scale and the mirror start to agree. Energy and drive tend to feel steadier through the whole afternoon, not just in short bursts.",
  },
  {
    label: "Day 90",
    offsetDays: 89,
    title: "Reaching Mars",
    body: "Look at the full 90-day picture, your waist, your chest, and how your clothes fit, then decide what you want to keep going.",
  },
] as const;

/**
 * Reframe opener, clause 1: reads the user's `exercise` answer back to him.
 * Ported verbatim from the approved prototype's `exerciseText()`. The
 * fall-through is deliberately the most generous reading -- a man who trains
 * rarely is still told he has been trying, because this screen's whole job is
 * to take the blame off him.
 */
const EXERCISE_OPENERS: Record<string, string> = {
  "4-plus": "You train, and you stay consistent about it.",
  "2-3": "You train, and you stay consistent about it.",
  weekly: "You still make time to train when you can.",
  rarely: "Even when training has been hard to fit in, you've been trying to change this.",
};

const EXERCISE_OPENER_FALLBACK = EXERCISE_OPENERS["rarely"] as string;

/** Reframe body paragraph 2, identical on every path. */
const REFRAME_NEXT_STEP =
  "A few more questions will show how long it's been happening and what your next 90 days should focus on.";

const REFRAME_TAIL =
  "And the belly and chest still refuse to change. Your answers may be pointing to a pattern that calories and workouts alone don't explain.";

function answerString(answers: Answers, key: string): string | undefined {
  const value = answers[key];
  return typeof value === "string" ? value : undefined;
}

function answerStrings(answers: Answers, key: string): readonly string[] {
  const value = answers[key];
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
}

function localNoon(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
}

function dateAtLocalNoon(isoDate: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day, 12, 0, 0, 0);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? date
    : null;
}

function calendarDayNumber(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY_MS;
}

function formatShortDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

/**
 * "September 18, 2026" -- the approved copy deck spells the month out in the
 * plan intro sentence, and reserves the abbreviated `formatShortDate` form
 * ("Sep 18") for the tight chart marker and milestone rows.
 */
function formatLongDate(date: Date): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

/** "September 18" -- the plan heading, which carries no year. */
function formatMonthDay(date: Date): string {
  return new Intl.DateTimeFormat("en-US", { month: "long", day: "numeric" }).format(date);
}

function joinLabels(labels: readonly string[]): string {
  if (labels.length === 0) return "";
  if (labels.length === 1) return labels[0] ?? "";
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
}

function weightedScore(answers: Answers): number {
  let score = BASE_SIGNAL_SCORE;
  const fatLocation = answerString(answers, "fat-location");
  const bellyDiet = answerString(answers, "belly-diet");
  const chestChange = answerString(answers, "chest-change");
  const impact = answerString(answers, "impact");
  const weightLook = answerString(answers, "weight-look");
  const duration = answerString(answers, "duration");

  if (fatLocation === "belly" || fatLocation === "chest") score += 8;
  if (bellyDiet === "last" || bellyDiet === "stalls" || bellyDiet === "everywhere-else") score += 8;
  if (chestChange === "yes" || chestChange === "little") score += 8;
  if (impact === "all" || impact === "energy" || impact === "sex-drive" || impact === "motivation") score += 7;
  if (weightLook === "smaller-soft" || weightLook === "not-lost") score += 7;
  if (duration === "years" || duration === "year") score += 7;

  return Math.min(MAX_SIGNAL_SCORE, score);
}

/**
 * Reported signal strength floors at "strong" -- the rule 12a analog for this
 * surface.
 *
 * A man only reaches this screen after telling us, across six questions, where
 * he carries fat, how his body answers a diet, whether his chest has changed,
 * and how long he has been fighting it. Reading that back as a "mild" or
 * "moderate" signal absolves the very problem he just described. It is the same
 * failure mode that removed STABLE from the persona result page and, twice,
 * de-absolved its softest verdict label (NORMAL -> MILD -> DETECTED). Operator
 * instruction, 2026-08-11: the reported strength is always strong or very
 * strong.
 *
 * The surviving boundary is the score that used to open the "strong" band, so
 * the two live bands split the reachable score space (48..93) down the middle
 * instead of collapsing nearly every path onto one label -- 87.5% of answer
 * combinations clear the old 48-point spread only partway. Reaching
 * VERY_STRONG_SIGNAL_SCORE means at least three of the six weighted families
 * fired, which is a cluster by any honest reading.
 *
 * "mild" and "moderate" stay in SIGNAL_BANDS as unreachable defensive values,
 * and the gauge still renders all four cells so "Strong" reads as a position
 * near the top of a scale rather than as the scale's ceiling.
 * tests/quizzes/weightloss-v2-signal-floor.test.ts walks the entire answer
 * space and fails if either value is ever produced.
 */
export function signalBand(score: number): SignalBand {
  return score >= VERY_STRONG_SIGNAL_SCORE ? "very-strong" : "strong";
}

function signalBandIndex(band: SignalBand): 0 | 1 | 2 | 3 {
  if (band === "moderate") return 1;
  if (band === "strong") return 2;
  if (band === "very-strong") return 3;
  return 0;
}

function buildRecap(answers: Answers): readonly RecapSegment[] {
  const location = FAT_LOCATIONS[answerString(answers, "fat-location") ?? ""];
  const duration = DURATION_LABELS[answerString(answers, "duration") ?? ""];
  const triedValues = answerStrings(answers, "tried");
  const tried = triedValues.includes("none")
    ? ""
    : joinLabels(triedValues.flatMap((value) => {
      const label = TRIED_LABELS[value];
      return label ? [label] : [];
    }));
  const goal = GOAL_LABELS[answerString(answers, "goal") ?? ""];

  if (!location && !duration && !tried && !goal) {
      return [{ text: "We’ll build your 90-day plan around the starting point you shared.", customerProvided: false }];
  }

  const recap: RecapSegment[] = [];
  if (location) {
    recap.push(
      { text: location.prefix, customerProvided: false },
      { text: location.label, customerProvided: true },
    );
  }
  if (duration) {
    recap.push(
      { text: location ? ", and you've been fighting it for " : "You've been fighting this for ", customerProvided: false },
      { text: duration, customerProvided: true },
    );
  }
  if (location || duration) recap.push({ text: ".", customerProvided: false });
  if (tried) {
    recap.push(
      { text: recap.length > 0 ? " You've already tried " : "You've already tried ", customerProvided: false },
      { text: tried, customerProvided: true },
      { text: ".", customerProvided: false },
    );
  }
  if (goal) {
    recap.push(
      { text: recap.length > 0 ? " The first thing you want back is " : "The first thing you want back is ", customerProvided: false },
      { text: goal, customerProvided: true },
      { text: ".", customerProvided: false },
    );
  }
  return recap;
}

/**
 * The approved copy deck's reframe screen, assembled from the user's own
 * `exercise` and `tried` answers so the absolution beat is true for every
 * path -- including a man who rarely trains and one who picked "None of these
 * yet". Pure function of `answers` (rule 2), wired through the `info` screen's
 * `deriveBody` seam.
 *
 * Ported from the prototype's `renderReframe()`:
 *   `${exerciseText()} ${triedText()}. ${tail}`
 *
 * Returns segments so the widget can mark the answer-derived runs -- the
 * prototype wraps them in `.answer-reference`, and shipping them as one flat
 * string erased the distinction. `customerProvided` is set on exactly the spans
 * that CHANGE with the answers: the whole opener sentence (it is selected by
 * `exercise`), and the tried clause's label list (or, when nothing was tried,
 * the affirming clause that replaces it, which is equally answer-selected).
 * The literal "You've tried " lead-in and the closing tail are authored on
 * every path and stay unmarked.
 */
export function buildReframeBody(answers: Answers): readonly (readonly RecapSegment[])[] {
  const exercise = answerString(answers, "exercise") ?? "";
  const opener = EXERCISE_OPENERS[exercise] ?? EXERCISE_OPENER_FALLBACK;

  const triedValues = answerStrings(answers, "tried");
  const triedLabels = triedValues.includes("none")
    ? []
    : triedValues.flatMap((value) => {
      const label = TRIED_LABELS[value];
      return label ? [label] : [];
    });
  // "None of these yet" (and an unanswered screen) must not produce "You've
  // tried ." -- the prototype swaps the whole clause for an effort-affirming
  // one rather than dropping it, so the sentence keeps its rhythm.
  const tried: readonly RecapSegment[] = triedLabels.length > 0
    ? [
      { text: "You've tried ", customerProvided: false },
      { text: joinLabels(triedLabels), customerProvided: true },
    ]
    : [{ text: "You've been looking for a way forward", customerProvided: true }];

  return [
    [
      { text: opener, customerProvided: true },
      { text: " ", customerProvided: false },
      ...tried,
      { text: `. ${REFRAME_TAIL}`, customerProvided: false },
    ],
    [{ text: REFRAME_NEXT_STEP, customerProvided: false }],
  ];
}

/** Flattens `buildReframeBody` output back to plain paragraph strings. Exported
 * for tests and for any server-side reader that wants the prose without the
 * presentation flags. */
export function reframeParagraphs(answers: Answers): readonly string[] {
  return buildReframeBody(answers).map((segments) => segments.map((s) => s.text).join(""));
}

function buildTarget(eventDate: string | undefined, today: Date): WeightlossV2Result["target"] {
  if (!eventDate || eventDate === "no-date") return null;
  const target = dateAtLocalNoon(eventDate);
  if (!target) return null;

  const daysFromToday = calendarDayNumber(target) - calendarDayNumber(today);
  const pinPercent = Math.min(91, Math.max(7, (daysFromToday / 90) * 100));
  return {
    isoDate: eventDate,
    shortLabel: formatShortDate(target),
    longLabel: formatLongDate(target),
    monthDayLabel: formatMonthDay(target),
    pinPercent,
    alignRight: pinPercent > 68,
  };
}

function buildMilestones(today: Date): readonly WeightlossMilestone[] {
  return MILESTONE_CONTENT.map(({ label, offsetDays, ...content }) => {
    const date = localNoon(today);
    date.setDate(date.getDate() + offsetDays);
    return { label, dateLabel: formatShortDate(date), ...content };
  });
}

export function deriveWeightlossV2Result(
  answers: Answers,
  today: Date = new Date(),
): WeightlossV2Result {
  const normalizedToday = localNoon(today);
  const score = weightedScore(answers);
  const band = signalBand(score);
  const target = buildTarget(answerString(answers, "event-date"), normalizedToday);

  return {
    signal: {
      band,
      bandIndex: signalBandIndex(band),
      message: `Your answers show a ${band.replace("-", " ")} cluster of signs linked to this pattern.`,
      bullets: [...SIGNAL_BULLETS],
    },
    recap: buildRecap(answers),
    target,
    planHeading: target
      ? `Your 90-Day Plan, Paced Around ${target.monthDayLabel}`
      : "Your 90-Day Plan Starts Today",
    planIntro: target
      ? [
        { text: "Your target date is ", customerProvided: false },
        // The date the user picked on the calendar, so it is echoed in Mars
        // orange like every other answer this surface reads back (the
        // prototype wraps it in `.answer-reference`). `WeightlossPlan` already
        // honored `customerProvided` here -- only the flag was wrong.
        { text: target.longLabel, customerProvided: true },
        { text: ". We built your plan around that finish line while keeping the full 90-day protocol in view.", customerProvided: false },
      ]
      : [{ text: "No fixed date yet. We built your plan around the next 90 days, starting today.", customerProvided: false }],
    milestones: buildMilestones(normalizedToday),
  };
}
